import Image from "next/image";
import Link from "next/link";
import type { ProcessedCourse } from "@/common/course/types";
import type { CourseRun } from "@/common/courseRun/types";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import CourseEnrollButton from "../ContentRefresh/CourseEnroll/CourseEnrollButtons";
import EnrollLink from "../ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import useTranslations from "@/i18n/useTranslations";

export interface CertificateOptionProps {
  auditCourseUrl?: string;
  certificateImageSrc?: string;
  course: ProcessedCourse;
  courseRun: CourseRun;
  partnerName?: string;
}

export default function CertificateOption({
  auditCourseUrl = "#",
  certificateImageSrc = "/bg-images/example-certificate-verified.webp",
  course,
  courseRun,
  partnerName,
}: CertificateOptionProps) {
  const t = useTranslations("Course");
  const tCommon = useTranslations("Common");
  const coursePrice = getCoursePrice(courseRun);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-16 rounded-xl bg-[#e8f5e9] py-6 px-4 md:px-12 md:py-6">
        <div className="flex flex-col gap-2 flex-1">
          <h3 className="font-bold text-primary-500 text-lg m-0">
            Verified certificate
          </h3>
          <p className="text-gray-800 text-base mb-0">
            Earn an official {partnerName} certificate to share on your resume
            and LinkedIn. Plus, unlock lifetime access to all course materials.
          </p>
          <div className="mt-2 md:mt-4 md:w-fit [&_button]:!text-white [&_button:hover]:!text-brand">
            {coursePrice ? (
              <CourseEnrollButton
                buttonClass="w-full sm:w-fit"
                course={course}
                courseRun={courseRun}
                location="course-details-certificate"
                price={coursePrice}
              />
            ) : (
              <EnrollLink
                buttonClass="w-full sm:w-fit"
                course={course}
                courseRun={courseRun}
                location="course-details-certificate"
              />
            )}
          </div>
        </div>
        <div className="relative flex-shrink-0 w-full md:w-[464px] aspect-[3/2] rounded overflow-hidden bg-white border border-putty-400">
          <Image
            src={certificateImageSrc}
            alt={tCommon("verifiedCertificatePreviewAlt")}
            fill
            className="object-cover"
          />
          <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-gray-400/50 rotate-[-20deg] select-none">
            {tCommon("preview")}
          </span>
        </div>
      </div>

      <div className="border border-putty-400 rounded-xl bg-white py-6 px-4 md:p-6">
        <h3 className="font-bold text-primary-500 text-lg m-0">
          {t("auditTrack")}
        </h3>
        <p className="text-gray-800 text-base mt-1 mb-0">
          Some audited courses offer only limited-time access to course
          materials. No certificate is provided.
          <Link href={auditCourseUrl} className="underline hover:no-underline">
            Audit course.
          </Link>
        </p>
      </div>
    </div>
  );
}
